import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/** Panel para gestionar tokens MCP (conectores ChatGPT / Claude Desktop).
 *
 * - Admin ve todos los tokens del estudio.
 * - Usuario normal ve sólo los suyos.
 * - Al crear un token, el valor se enseña UNA SOLA VEZ con botón de copiar.
 */
export default function MCPTokensPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [tokens, setTokens] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Create form
  const [label, setLabel] = useState("ChatGPT connector");
  const [forUserId, setForUserId] = useState("");
  // Newly created token (shown once)
  const [newToken, setNewToken] = useState(null);

  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await api.get("/mcp-tokens");
      setTokens(r.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const r = await api.get("/admin/users");
      setAllUsers(r.data || []);
    } catch { /* silent */ }
  }, [isAdmin]);

  useEffect(() => { load(); loadUsers(); }, [load, loadUsers]);

  const createToken = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = { label: label.trim() || "ChatGPT connector" };
      if (forUserId) payload.user_id = forUserId;
      const r = await api.post("/mcp-tokens", payload);
      setNewToken(r.data);
      setLabel("ChatGPT connector");
      setForUserId("");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const revokeToken = async (tid) => {
    if (!window.confirm("¿Revocar este token? ChatGPT dejará de poder conectar inmediatamente.")) return;
    setErr(""); setBusy(true);
    try {
      await api.delete(`/mcp-tokens/${tid}`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("Copiado al portapapeles");
    } catch {
      alert("No se pudo copiar. Selecciónalo y copia manualmente.");
    }
  };

  const mcpUrl = `${window.location.origin}/api/mcp/`;

  return (
    <section className="mt-10 border-t border-stone-200 pt-8" data-testid="mcp-tokens-panel">
      <div className="mb-5">
        <h2 className="text-lg font-serif text-stone-900">
          Conectores MCP · ChatGPT / Claude
        </h2>
        <p className="text-sm text-stone-600 mt-1 max-w-2xl">
          Genera tokens para que ChatGPT (o Claude Desktop) puedan trabajar sobre Grabarte 360 como herramienta MCP:
          listar proyectos, tareas, convocatorias, festivales y editarlos. <strong>Guion y dossier son de sólo lectura</strong> desde el conector.
        </p>
      </div>

      <div className="rounded-lg border border-stone-200 bg-stone-50 p-4 mb-5 text-sm">
        <div className="text-[11px] uppercase tracking-[0.3em] text-stone-500 mb-2">Cómo conectar ChatGPT</div>
        <ol className="list-decimal list-inside space-y-1 text-stone-700 text-sm">
          <li>Pulsa <strong>"Generar token"</strong> abajo y copia la URL que aparece.</li>
          <li>En ChatGPT: <em>Ajustes → Conectores → Agregar conector MCP</em>.</li>
          <li>Pega la URL. En <strong>Autenticación</strong> elige <strong>"Sin autenticación"</strong> (el token va dentro de la URL).</li>
          <li>Guardar. ChatGPT verá las 23 herramientas del estudio.</li>
        </ol>
      </div>

      {err && <p className="mb-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="mcp-err">{err}</p>}

      {/* Create form */}
      <div className="rounded-lg border border-stone-200 bg-white p-4 mb-6">
        <h3 className="text-[11px] uppercase tracking-[0.3em] text-stone-500 mb-3">Crear nuevo token</h3>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="flex-1 min-w-[220px] text-sm">
            <span className="block text-[11px] uppercase tracking-[0.3em] text-stone-500 mb-1">Nombre / dispositivo</span>
            <input
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="ChatGPT Eduardo"
              className="w-full rounded-md border border-stone-300 px-3 py-2 outline-none focus:border-[#26775E]"
              data-testid="mcp-new-label"
            />
          </label>
          {isAdmin && (
            <label className="min-w-[220px] text-sm">
              <span className="block text-[11px] uppercase tracking-[0.3em] text-stone-500 mb-1">Para usuario</span>
              <select
                value={forUserId}
                onChange={e => setForUserId(e.target.value)}
                className="w-full rounded-md border border-stone-300 px-3 py-2 outline-none focus:border-[#26775E]"
                data-testid="mcp-new-user"
              >
                <option value="">Yo ({user?.email})</option>
                {allUsers.filter(u => u.user_id !== user?.user_id).map(u => (
                  <option key={u.user_id} value={u.user_id}>{u.email}</option>
                ))}
              </select>
            </label>
          )}
          <button
            onClick={createToken}
            disabled={busy || !label.trim()}
            className="rounded-md bg-[#26775E] px-4 py-2 text-sm text-white hover:bg-[#1e604b] disabled:opacity-50"
            data-testid="mcp-create-btn"
          >
            {busy ? "Creando…" : "Generar token"}
          </button>
        </div>
      </div>

      {/* Newly created token banner */}
      {newToken && (
        <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-4 mb-6" data-testid="mcp-new-token-banner">
          <div className="text-sm text-amber-900 mb-3">
            <strong>Token generado.</strong> Guárdalo ahora: no podrás volver a verlo después.
          </div>

          {/* RECOMMENDED: path-based URL for ChatGPT "no authentication" mode */}
          <div className="mb-3">
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-800 mb-1">
              URL recomendada para ChatGPT (sin autenticación)
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-xs bg-white border border-amber-300 rounded px-3 py-2 text-stone-800 break-all" data-testid="mcp-path-url">
                {`${window.location.origin}/api/mcp/t/${newToken.token}/`}
              </code>
              <button
                onClick={() => copyToClipboard(`${window.location.origin}/api/mcp/t/${newToken.token}/`)}
                className="shrink-0 rounded-md bg-amber-600 px-3 py-2 text-xs text-white hover:bg-amber-500"
                data-testid="mcp-copy-path-url-btn"
              >Copiar URL</button>
            </div>
            <div className="text-[11px] text-amber-800 mt-1">
              En ChatGPT elige <strong>"Sin autenticación"</strong>. El token va dentro de la URL.
            </div>
          </div>

          {/* Alternative: query param */}
          <details className="mb-3">
            <summary className="text-[11px] uppercase tracking-[0.3em] text-stone-600 cursor-pointer hover:text-stone-800">
              Alternativas (clientes avanzados)
            </summary>
            <div className="mt-2 space-y-3">
              <div>
                <div className="text-[11px] text-stone-500 mb-1">URL con query param (?token=…)</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-[11px] bg-white border border-stone-300 rounded px-2 py-1 text-stone-700 break-all" data-testid="mcp-full-url">
                    {`${mcpUrl}?token=${newToken.token}`}
                  </code>
                  <button
                    onClick={() => copyToClipboard(`${mcpUrl}?token=${newToken.token}`)}
                    className="shrink-0 rounded-md bg-stone-600 px-2 py-1 text-[11px] text-white hover:bg-stone-500"
                    data-testid="mcp-copy-full-url-btn"
                  >Copiar</button>
                </div>
              </div>
              <div>
                <div className="text-[11px] text-stone-500 mb-1">Solo el token (Bearer separado para Claude Desktop, etc.)</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-[11px] bg-white border border-stone-300 rounded px-2 py-1 text-stone-700 break-all">
                    {newToken.token}
                  </code>
                  <button
                    onClick={() => copyToClipboard(newToken.token)}
                    className="shrink-0 rounded-md bg-stone-600 px-2 py-1 text-[11px] text-white hover:bg-stone-500"
                    data-testid="mcp-copy-token-btn"
                  >Copiar</button>
                </div>
              </div>
            </div>
          </details>

          <button
            onClick={() => setNewToken(null)}
            className="text-xs text-stone-600 hover:text-stone-800 underline"
          >Entendido, lo he guardado</button>
        </div>
      )}

      {/* Tokens list */}
      <div className="rounded-lg border border-stone-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-100 text-[11px] uppercase tracking-[0.2em] text-stone-500">
            <tr>
              <th className="text-left px-3 py-2">Nombre</th>
              {isAdmin && <th className="text-left px-3 py-2">Usuario</th>}
              <th className="text-left px-3 py-2">Creado</th>
              <th className="text-left px-3 py-2">Último uso</th>
              <th className="text-left px-3 py-2">Estado</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {tokens.length === 0 && (
              <tr><td colSpan={isAdmin ? 6 : 5} className="px-3 py-6 text-center text-stone-500">
                No hay tokens aún. Genera uno arriba.
              </td></tr>
            )}
            {tokens.map(t => {
              const isRevoked = !!t.revoked_at;
              return (
                <tr key={t.id} className={`border-t border-stone-200 ${isRevoked ? "opacity-50" : ""}`} data-testid={`mcp-token-row-${t.id}`}>
                  <td className="px-3 py-2 text-stone-800">{t.label}</td>
                  {isAdmin && <td className="px-3 py-2 text-stone-600">{t.user_email || t.user_id}</td>}
                  <td className="px-3 py-2 text-stone-500 text-xs">
                    {t.created_at ? new Date(t.created_at).toLocaleString("es") : "—"}
                  </td>
                  <td className="px-3 py-2 text-stone-500 text-xs">
                    {t.last_used_at ? new Date(t.last_used_at).toLocaleString("es") : <span className="text-stone-400">Nunca</span>}
                  </td>
                  <td className="px-3 py-2">
                    {isRevoked
                      ? <span className="text-red-600 text-xs">Revocado</span>
                      : <span className="text-emerald-700 text-xs">Activo</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!isRevoked && (
                      <button
                        onClick={() => revokeToken(t.id)}
                        className="text-xs text-red-600 hover:text-red-700 underline"
                        data-testid={`mcp-revoke-${t.id}`}
                      >Revocar</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
